/** @fileoverview Joined segmented control for a choice input, with a white pill that slides to the picked option. */

import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';

/** Where the pill sits inside the track, in pixels. */
type PillBox = { x: number; y: number; width: number; height: number };

type SegmentedControlProps = {
  controlId: string;
  label: string;
  required?: boolean;
  describedBy?: string;
  invalid: boolean;
  options: { key: string; label: string }[];
  selectionPaths?: (string | undefined)[];
  selectionDefinitionPaths?: (string | undefined)[];
  selectedKey?: string;
  onPick: (key: string) => void;
};

/**
 * Tells whether two pill boxes are the same, so a measure that found no change does not render again.
 * @param first the box shown now
 * @param second the box just measured
 */
const isSameBox = (first: PillBox | null, second: PillBox | null) =>
  first === second ||
  Boolean(
    first &&
      second &&
      first.x === second.x &&
      first.y === second.y &&
      first.width === second.width &&
      first.height === second.height
  );

/**
 * Shows the options as one grey track. The picked option sits on a white pill that slides from
 * the old choice to the new one; the pill follows the option when the track or an option resizes.
 * @param props the control id, label, options, picked key and pick handler
 */
export default function SegmentedControl({
  controlId,
  label,
  required,
  describedBy,
  invalid,
  options,
  selectionPaths,
  selectionDefinitionPaths,
  selectedKey,
  onPick,
}: SegmentedControlProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<PillBox | null>(null);
  const optionKeys = options
    .map(({ key, label: optionLabel }) => `${key}:${optionLabel}`)
    .join('\n');

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return undefined;
    }
    /** Reads where the picked option sits now. */
    const measure = () => {
      const item = track.querySelector<HTMLElement>('[data-pressed]');
      const box = item
        ? {
            x: item.offsetLeft,
            y: item.offsetTop,
            width: item.offsetWidth,
            height: item.offsetHeight,
          }
        : null;
      setPill((current) => (isSameBox(current, box) ? current : box));
    };
    measure();
    /* Each option is watched too: a new label or a late font changes it, not the track. */
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    Array.from(track.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [optionKeys, selectedKey]);

  return (
    <ToggleGroup
      ref={trackRef}
      id={controlId}
      aria-label={label}
      aria-required={required || undefined}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      multiple={false}
      value={selectedKey ? [selectedKey] : []}
      onValueChange={(values) => {
        if (values.length > 0) onPick(values[0]);
      }}
      spacing={0.5}
      className="relative max-w-full flex-wrap rounded-lg border border-border bg-muted p-0.5 aria-invalid:border-destructive"
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute top-0 left-0 h-(--segment-height) w-(--segment-width) translate-x-(--segment-x) translate-y-(--segment-y) rounded-md bg-card shadow-sm transition-all duration-200 ease-out motion-reduce:transition-none ${
          pill ? 'opacity-100' : 'opacity-0'
        }`}
        style={
          {
            '--segment-x': `${pill?.x ?? 0}px`,
            '--segment-y': `${pill?.y ?? 0}px`,
            '--segment-width': `${pill?.width ?? 0}px`,
            '--segment-height': `${pill?.height ?? 0}px`,
          } as CSSProperties
        }
      />
      {options.map((option, index) => (
        <ToggleGroupItem
          key={option.key}
          value={option.key}
          data-fabrik-selection-path={selectionPaths?.[index]}
          data-fabrik-selection-definition-path={selectionDefinitionPaths?.[index]}
          className="relative h-app-control-compact rounded-md px-3 type-body font-normal text-muted-foreground transition-colors duration-200 hover:bg-transparent hover:text-foreground data-[pressed]:bg-transparent data-[pressed]:font-medium data-[pressed]:text-foreground"
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
